import { useCallback, useEffect, useRef } from "react";

/**
 * Brings the list-page filter panel into view when the user toggles
 * "Show Filters" / "Hide Filters".
 *
 * The filter panel renders ABOVE the toggle button, so expanding it grows the
 * document above the click point. The browser keeps the clicked button in place,
 * which pushes the freshly expanded filters up behind the sticky app header when
 * the page is already scrolled. The scroll is applied only when the target is not
 * comfortably visible, so pages opened at the top keep their current behaviour.
 */

const HEADER_GAP = 16;
const FALLBACK_HEADER_HEIGHT = 56;
const MIN_VISIBLE_HEIGHT = 120;
const SCROLL_DURATION = 520;
const SETTLE_DURATION = 180;
const SETTLE_THRESHOLD = 2;

type PendingScroll = "show" | "hide" | null;

const easeInOutCubic = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

const prefersReducedMotion = () =>
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const getStickyHeaderOffset = () => {
  const header = document.querySelector<HTMLElement>(".MuiAppBar-root");
  const headerHeight = header?.getBoundingClientRect().height || FALLBACK_HEADER_HEIGHT;
  return headerHeight + HEADER_GAP;
};

const getScrollTargetFor = (element: HTMLElement) => {
  const desiredTop = element.getBoundingClientRect().top + window.scrollY - getStickyHeaderOffset();
  const maxTop = Math.max(document.documentElement.scrollHeight - window.innerHeight, 0);
  return Math.min(Math.max(desiredTop, 0), maxTop);
};

const isComfortablyVisible = (element: HTMLElement) => {
  const { top } = element.getBoundingClientRect();
  return top >= getStickyHeaderOffset() && top <= window.innerHeight - MIN_VISIBLE_HEIGHT;
};

export const useFilterPanelFocus = (isOpen: boolean, setIsOpen: (open: boolean) => void) => {
  const filterPanelRef = useRef<HTMLDivElement | null>(null);
  const pendingScrollRef = useRef<PendingScroll>(null);
  const isSettlingRef = useRef(false);
  const takenOverByUserRef = useRef(false);
  const frameRef = useRef<number | null>(null);
  const cancelAnimationRef = useRef<() => void>(() => undefined);

  // A hand-rolled eased scroll instead of `behavior: "smooth"`: it lets the
  // travel time match the Collapse animation so the expand and the scroll read
  // as one movement, and it can be cancelled the moment the user takes over.
  const animateScrollTo = useCallback((targetTop: number, duration: number) => {
    cancelAnimationRef.current();

    const startTop = window.scrollY;
    const distance = targetTop - startTop;
    if (Math.abs(distance) < SETTLE_THRESHOLD) return;

    if (prefersReducedMotion() || typeof window.requestAnimationFrame !== "function") {
      window.scrollTo({ top: targetTop });
      return;
    }

    const stop = () => {
      if (frameRef.current !== null) window.cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
      window.removeEventListener("wheel", takeOver);
      window.removeEventListener("touchstart", takeOver);
      window.removeEventListener("keydown", takeOver);
      cancelAnimationRef.current = () => undefined;
    };
    const takeOver = () => {
      takenOverByUserRef.current = true;
      stop();
    };
    cancelAnimationRef.current = stop;

    takenOverByUserRef.current = false;
    window.addEventListener("wheel", takeOver, { passive: true });
    window.addEventListener("touchstart", takeOver, { passive: true });
    window.addEventListener("keydown", takeOver);

    const startTime = performance.now();
    const step = (now: number) => {
      const progress = Math.min((now - startTime) / duration, 1);
      window.scrollTo({ top: startTop + distance * easeInOutCubic(progress) });
      if (progress < 1) {
        frameRef.current = window.requestAnimationFrame(step);
        return;
      }
      stop();
    };
    frameRef.current = window.requestAnimationFrame(step);
  }, []);

  useEffect(() => () => cancelAnimationRef.current(), []);

  // Only a user click arms the scroll, so pages that render with the filters
  // already open (or open them from a deep link) are left untouched.
  const toggleFilterPanel = useCallback(() => {
    pendingScrollRef.current = isOpen ? "hide" : "show";
    setIsOpen(!isOpen);
  }, [isOpen, setIsOpen]);

  // Scroll alongside the expand rather than after it. The panel's own position in
  // the document does not move while it grows, so the target is already known.
  const handleFilterPanelEntering = useCallback(() => {
    if (pendingScrollRef.current !== "show") return;
    pendingScrollRef.current = null;

    const panel = filterPanelRef.current;
    if (!panel || isComfortablyVisible(panel)) return;

    isSettlingRef.current = true;
    animateScrollTo(getScrollTargetFor(panel), SCROLL_DURATION);
  }, [animateScrollTo]);

  // Absorb any drift left by the browser's scroll anchoring once the panel has
  // reached its full height. Usually a no-op, otherwise a few imperceptible px.
  const handleFilterPanelEntered = useCallback(() => {
    if (!isSettlingRef.current) return;
    isSettlingRef.current = false;
    if (takenOverByUserRef.current) return;

    const panel = filterPanelRef.current;
    if (panel && frameRef.current === null) animateScrollTo(getScrollTargetFor(panel), SETTLE_DURATION);
  }, [animateScrollTo]);

  // Target the collapsed panel rather than the toggle: the toggle stacks BELOW
  // the page title on narrow widths, so aligning it to the header would hide the
  // title behind it. The panel sits above the whole list card at every width.
  const handleFilterPanelExited = useCallback(() => {
    if (pendingScrollRef.current !== "hide") return;
    pendingScrollRef.current = null;

    const panel = filterPanelRef.current;
    if (!panel || isComfortablyVisible(panel)) return;

    animateScrollTo(getScrollTargetFor(panel), SCROLL_DURATION);
  }, [animateScrollTo]);

  return {
    filterPanelRef,
    toggleFilterPanel,
    handleFilterPanelEntering,
    handleFilterPanelEntered,
    handleFilterPanelExited,
  };
};
